/* eslint-disable react-hooks/exhaustive-deps */
import * as THREE from "three";
import React, { useMemo, useState } from 'react';
import { useThree } from '@react-three/fiber';

import { const_var, params } from '../../../../../../redux/reducers/reducer';
import { controls } from "../../../../../../redux/reducers/common/eventReducer";

import { extrudeSettings, roofPattern, roofPatternCurve, textureAnisotropy } from '../../../../../../utils/Functions';
import { CSG } from "../../../../../../assets/libs/csg-lib";
import useMouseEvents from "../../../../../../hooks/useMouseEvents";
import { MaterialInnerRoof, MaterialOuterRoof, MaterialRoofRidge } from "../../../../CustomMaterial";

const RoofModel = () => {
    const { gl } = useThree();
    
    const [angle, setAngle] = useState(0);
    const [overHangWidth, setOverHangWidth] = useState(0);
    const [overHangLength, setOverHangLength] = useState(0);
    const [isEqualLeftHeight, setIsEqualLeftHeight] = useState(false);
    const [isEqualRightHeight, setIsEqualRightHeight] = useState(false);
    const [isEqualHeight, setIsEqualHeight] = useState(false);
    const [isEqualLeftLeanHeight, setIsEqualLeftLeanHeight] = useState(false);
    const [isEqualRightLeanHeight, setIsEqualRightLeanHeight] = useState(false);
    const [overHangHorizon, setOverHangHorizon] = useState(0);
    const [roofWidthSize, setRoofWidthSize] = useState(Math.sqrt(Math.pow(params.leanF_p_w, 2) + Math.pow((params.leanF_p_w / 12 * params.b_l_t_r_pF), 2)))
    const [patternLength, setPatternLength] = useState(0);
    const [patternDepth, setPatternDepth] = useState(params.leanF_p_d);
    const [patternPosition, setPatternPosition] = useState({x:0,y:0,z:0});
    const [curveRoofPositionX, setCurveRoofPositionX] = useState(0)
    const [curveRoofPatternDepth, setCurveRoofPatternDepth] = useState(0)

    const trimThickness = 0.2;
    const roofThickness = 0.005;
    const offset = 0.12
    const cbFrontBackWallOffset = 0.16;
    const ridgeThickness = 0.5

    const texture_metalSprite = const_var.metalTexture;
    const verticalMap = (texture_metalSprite) ? texture_metalSprite.clone() : texture_metalSprite;
    const horizontalMap = (texture_metalSprite) ? texture_metalSprite.clone() : texture_metalSprite;
    if(horizontalMap && verticalMap){
        textureAnisotropy(gl, verticalMap, 1.3, 1.3, Math.PI);
        verticalMap.needsUpdate = true;
        horizontalMap.offset.x = 0.45;
        textureAnisotropy(gl, horizontalMap, 1.3, 1.3, Math.PI / 2);
        horizontalMap.needsUpdate = true;
    }

    const diagonalLength = roofWidthSize + overHangWidth;
    const trimOffsetHeight = params.p_r_p / 12 * trimThickness;
    const leanRoofLeftHeight = (params.b_l_t_r_p / 12) * params.lean_p_w;
    const leanRoofRightHeight = (params.b_l_t_r_pR / 12) * params.leanR_p_w;
    const leanRoofHeight = (params.b_l_t_r_pF / 12) * params.leanF_p_w;
    const roofCutShapeWidth = Math.sqrt(Math.pow(params.leanF_p_w, 2) * 2);

    const canvasElement = document.getElementsByClassName("buildingcanvas")
    const isPattern = (canvasElement != undefined && canvasElement[0]?.clientWidth > 1050 ) ? true : false

    const offSetValue = useMemo(() => {
        let offSetValue;
        if (params.p_r_s === 1) {
            const height =  Math.sin(angle) * offset;
            offSetValue = (height == 0 || Math.tan(angle) == 0) ? 0 : height / Math.tan(angle)
        } else offSetValue = 0
        
        return offSetValue
    }, [angle, params.p_r_s])    

    const roofTexture = useMemo(() => {
        if (params.p_r_s === 1 || params.p_r_s === 2) return verticalMap;
        else if (params.p_r_s === 3) return horizontalMap;
    }, [horizontalMap, params.p_r_s, verticalMap]);
    
    const roofRidgeOffsetHeight = useMemo(() => (0.03 / Math.cos(angle)), [angle])

    const lengthLeftOffset = useMemo(() => (
        (isEqualLeftLeanHeight && !isEqualRightLeanHeight && (params.p_w === params.leanF_p_d || Number(params.leantoAlignmentFront) === 2)) ||
        (isEqualLeftLeanHeight && isEqualRightLeanHeight && (params.p_w !== params.leanF_p_d && Number(params.leantoAlignmentFront) === 2)) ||
        (isEqualLeftLeanHeight && isEqualRightLeanHeight && (params.p_w === params.leanF_p_d))
    ) ? 0.218 : overHangLength, [isEqualLeftLeanHeight, isEqualRightLeanHeight, isEqualHeight, params.p_w, params.leanF_p_d, params.leantoAlignmentFront, overHangLength ]);
    
    const lengthRightOffset = useMemo(() => (
        (!isEqualLeftLeanHeight && isEqualRightLeanHeight && (params.p_w === params.leanF_p_d || Number(params.leantoAlignmentFront) === 3)) ||
        (isEqualLeftLeanHeight && isEqualRightLeanHeight && (params.p_w !== params.leanF_p_d && Number(params.leantoAlignmentFront) === 3)) ||
        (isEqualLeftLeanHeight && isEqualRightLeanHeight && (params.p_w === params.leanF_p_d))
    ) ? 0.218 : overHangLength, [isEqualLeftLeanHeight, isEqualRightLeanHeight, isEqualHeight, params.p_w, params.leanF_p_d, params.leantoAlignmentFront, overHangLength]);
    
    const patternLengthOffset = useMemo(() => {
        let offset = 0;
        if((isEqualLeftLeanHeight && !isEqualRightLeanHeight && (params.p_w === params.leanF_p_d || Number(params.leantoAlignmentFront) === 2)) ||
            (isEqualLeftLeanHeight && isEqualRightLeanHeight && (params.p_w !== params.leanF_p_d && Number(params.leantoAlignmentFront) === 2)) ||
            (!isEqualLeftLeanHeight && isEqualRightLeanHeight && (params.p_w === params.leanF_p_d || Number(params.leantoAlignmentFront) === 3)) ||
            (isEqualLeftLeanHeight && isEqualRightLeanHeight && (params.p_w !== params.leanF_p_d && Number(params.leantoAlignmentFront) === 3))){
                offset =  -(overHangLength - 0.22);
        } else if(isEqualLeftLeanHeight && isEqualRightLeanHeight && (params.p_w === params.leanF_p_d)){
                offset = -(overHangLength - 0.22) * 2;
        } else offset = 0;
        return offset;

    }, [isEqualLeftLeanHeight, isEqualRightLeanHeight, isEqualHeight, params.p_w, params.leanF_p_d, params.leantoAlignmentFront, overHangLength]);

    const patternPositionOffset = useMemo(() => {
        let offset = 0;
        if((isEqualRightLeanHeight && (params.p_w === params.leanF_p_d || Number(params.leantoAlignmentFront) === 3)) 
            || (isEqualLeftLeanHeight && isEqualRightLeanHeight && (params.p_w !== params.leanF_p_d && Number(params.leantoAlignmentFront) === 3))){
            offset = -(overHangLength - 0.22)
        } else offset = 0;
        
        return offset;
    }, [isEqualLeftLeanHeight, isEqualRightLeanHeight, isEqualHeight, params.p_w, params.leanF_p_d, params.leantoAlignmentFront, overHangLength])

    const regularPatternPositionOffset = useMemo(() => {
        let offset = 0;
        if((isEqualLeftLeanHeight && (Number(params.leantoAlignmentFront) === 2 || params.p_w === params.leanF_p_d))){
            offset = (overHangLength - 0.22)
        } else offset = 0;

        return offset;
    }, [isEqualLeftLeanHeight, isEqualRightLeanHeight, isEqualHeight, params.p_w, params.leanF_p_d, params.leantoAlignmentFront, overHangLength])

    const regularHeightOffset = useMemo(() => {
        return (params.p_r_s === 1 ? const_var.roofRegularDst * 2 + 0.15 * params.b_l_t_r_pF / 12 - 0.01: 0)
    }, [params.p_r_s, params.b_l_t_r_pF])

useMemo(() => { setOverHangHorizon(Math.cos(angle) * overHangWidth) }, [angle, overHangWidth])

    let sideOverHangSize = 0 
    let endOverHangSize = 0 
    if(const_var.isOverhang['side']!=undefined && const_var.isOverhang['side'].size){
        sideOverHangSize = Number(const_var.isOverhang['side'].size)
        if(sideOverHangSize<=1){
            sideOverHangSize = (0.25 + sideOverHangSize)
        }
    }
    if(const_var.isOverhang['end']!=undefined && const_var.isOverhang['end'].size){
        endOverHangSize = Number(const_var.isOverhang['end'].size)
    }

    const roofPanModel = useMemo(() => {
        let offset = 0;
        if (params.p_r_s === 1) offset = const_var.roofRegularDst / Math.cos(Math.atan(params.b_l_t_r_pF / 12));
        else offset = 0;
        
        const model = new THREE.Shape();

        if( params.add_left_front_lean_porch !== true && params.add_right_front_lean_porch !== true ) {
            model.moveTo(-(params.leanF_p_d / 2 + lengthLeftOffset - 0.15), 0 + overHangWidth - offset);
            model.lineTo((params.leanF_p_d / 2 + lengthRightOffset - 0.15), 0 + overHangWidth - offset);
            model.lineTo((params.leanF_p_d / 2 + lengthRightOffset - 0.15), -roofWidthSize);
            model.lineTo(-(params.leanF_p_d / 2 + lengthLeftOffset - 0.15), -roofWidthSize);
        
        } else if( params.add_left_front_lean_porch === true && params.add_right_front_lean_porch !== true ) {
            model.moveTo(-(params.leanF_p_d / 2 + overHangHorizon + params.leanF_p_w - offSetValue), 0 + overHangWidth - offset);
            model.lineTo((params.leanF_p_d / 2 + lengthRightOffset - 0.15), 0 + overHangWidth - offset);
            model.lineTo((params.leanF_p_d / 2 + lengthRightOffset - 0.15), -roofWidthSize);
            model.lineTo(-(params.leanF_p_d / 2), -roofWidthSize);

        } else if( params.add_left_front_lean_porch !== true && params.add_right_front_lean_porch === true ) {
            model.moveTo((params.leanF_p_d / 2 + params.leanF_p_w + overHangHorizon - offSetValue), 0 + overHangWidth - offset);
            model.lineTo(-(params.leanF_p_d / 2 + lengthLeftOffset - 0.15), 0 + overHangWidth - offset);
            model.lineTo(-(params.leanF_p_d / 2 + lengthLeftOffset - 0.15), -roofWidthSize);
            model.lineTo((params.leanF_p_d / 2), -roofWidthSize);

        } else {
            model.moveTo(-(params.leanF_p_d / 2 + overHangHorizon + params.leanF_p_w - offSetValue), 0 + overHangWidth - offset);
            model.lineTo((params.leanF_p_d / 2 + overHangHorizon + params.leanF_p_w - offSetValue), 0 + overHangWidth - offset);
            model.lineTo((params.leanF_p_d / 2), -roofWidthSize);
            model.lineTo(-(params.leanF_p_d / 2), -roofWidthSize);
        }

        model.closePath();

        return model;
    }, [params.p_r_s, params.add_left_front_lean_porch,params.leanF_p_w, params.add_right_front_lean_porch, params.leanF_p_d, overHangHorizon, roofWidthSize, params.b_l_t_r_pF, overHangLength, overHangWidth, offSetValue, lengthLeftOffset, lengthRightOffset]);

    const roofTopTrimRidgeModelObj = useMemo(() => {
        let rightTrimRidge;
        let leftTrimRidge;

        const roofTopRightTrimRidgeModel = new THREE.Shape();
            roofTopRightTrimRidgeModel.moveTo((params.leanF_p_d / 2) + params.leanF_p_w + overHangHorizon - ridgeThickness, 0 + overHangWidth);
            roofTopRightTrimRidgeModel.lineTo((params.leanF_p_d / 2) + params.leanF_p_w + overHangHorizon, 0 + overHangWidth);
            roofTopRightTrimRidgeModel.lineTo((params.leanF_p_d / 2), -roofWidthSize);
            roofTopRightTrimRidgeModel.lineTo((params.leanF_p_d / 2 - ridgeThickness), -roofWidthSize);
            roofTopRightTrimRidgeModel.closePath();

        const roofTopLeftTrimRidgeModel = new THREE.Shape();
            roofTopLeftTrimRidgeModel.moveTo(-((params.leanF_p_d / 2) + params.leanF_p_w + overHangHorizon), 0 + overHangWidth);
            roofTopLeftTrimRidgeModel.lineTo(-((params.leanF_p_d / 2) + params.leanF_p_w + overHangHorizon) + ridgeThickness, 0 + overHangWidth);
            roofTopLeftTrimRidgeModel.lineTo(-(params.leanF_p_d / 2) + ridgeThickness, -roofWidthSize);
            roofTopLeftTrimRidgeModel.lineTo(-(params.leanF_p_d / 2), -roofWidthSize);
            roofTopLeftTrimRidgeModel.closePath();

        const roofHeight = params.p_r_p / 12 * (params.p_w / 2 + 0.75)

        let lengthOffset;
        let delModelPosOffsetX;

        if(params.o_v_h_n_g_e && params.o_v_h_n_g) {
            if(endOverHangSize === 2 && sideOverHangSize === 1.05){
                if(params.p_r_s === 2){
                    lengthOffset = 2 + endOverHangSize / 2;
                } else if(params.p_r_s === 3){
                    lengthOffset = 2 + endOverHangSize;
                }
                delModelPosOffsetX = 1.05;
            } else if(endOverHangSize === 2 && sideOverHangSize === 2){
                if(params.p_r_s === 2){
                    lengthOffset = 2 + endOverHangSize / 2;
                } else if(params.p_r_s === 3){
                    lengthOffset = 2 + endOverHangSize;
                }
                delModelPosOffsetX = 2;
            } else if(endOverHangSize === 1 && sideOverHangSize === 2){
                lengthOffset = 2;
                delModelPosOffsetX = 2;
            }else {
                lengthOffset = 2;
                delModelPosOffsetX = 1.25;
            }
        } else if(!params.o_v_h_n_g_e && params.o_v_h_n_g) {
            if(sideOverHangSize === 2) {
                delModelPosOffsetX = 2;
            } else{
                delModelPosOffsetX = 1.25;
            }
            lengthOffset = 1.5;
        } else if(params.o_v_h_n_g_e && !params.o_v_h_n_g) {
            if(endOverHangSize === 2){
                if(params.p_r_s === 2){
                    lengthOffset = 2 + endOverHangSize / 2;
                } else if(params.p_r_s === 3){
                    lengthOffset = 2 + endOverHangSize;
                }
            } else {
                lengthOffset = 2;
            }
            delModelPosOffsetX = 0.75;
        } else {
            lengthOffset = 1.5
            delModelPosOffsetX = 0.75;
        }
    
        const roofDelModel = new THREE.Shape();
        roofDelModel.moveTo(params.p_w / 2 + delModelPosOffsetX, params.p_h);
        roofDelModel.lineTo(0, params.p_h + roofHeight);
        roofDelModel.lineTo(0, 0);
        roofDelModel.lineTo(params.p_w / 2 + delModelPosOffsetX, 0);
        roofDelModel.closePath();

        const meshRoofTopRightTrimRidgeModel = new THREE.Mesh(new THREE.ExtrudeGeometry(roofTopRightTrimRidgeModel, extrudeSettings(0.03)), new THREE.MeshStandardMaterial({color: 'blue', side : THREE.DoubleSide}));
        const meshRoofTopLeftTrimRidgeModel = new THREE.Mesh(new THREE.ExtrudeGeometry(roofTopLeftTrimRidgeModel, extrudeSettings(0.03)), new THREE.MeshStandardMaterial({color: 'blue', side : THREE.DoubleSide}));
        const roofDelModelObj = new THREE.Mesh(new THREE.ExtrudeGeometry(roofDelModel, extrudeSettings(params.p_d + lengthOffset)), new THREE.MeshStandardMaterial({color: 'blue', side : THREE.DoubleSide}));
         
        const meshRoofTopRightTrimRidgeModel_Temp = meshRoofTopRightTrimRidgeModel.clone();
        meshRoofTopRightTrimRidgeModel_Temp.position.set(0, params.leanF_p_h + roofRidgeOffsetHeight + 0.068, params.p_d / 2 + params.leanF_p_w + cbFrontBackWallOffset);
        meshRoofTopRightTrimRidgeModel_Temp.rotateX(Math.PI / 2 + angle);
        meshRoofTopRightTrimRidgeModel_Temp.updateMatrixWorld();

        const meshRoofTopLeftTrimRidgeModel_Temp = meshRoofTopLeftTrimRidgeModel.clone();
        meshRoofTopLeftTrimRidgeModel_Temp.position.set(0, params.leanF_p_h + roofRidgeOffsetHeight + 0.068, params.p_d / 2 + params.leanF_p_w + cbFrontBackWallOffset);
        meshRoofTopLeftTrimRidgeModel_Temp.rotateX(Math.PI / 2 + angle);
        meshRoofTopLeftTrimRidgeModel_Temp.updateMatrixWorld();

        const roofLeftDelModelObj_Temp = roofDelModelObj.clone();
        roofLeftDelModelObj_Temp.position.set(-params.leanF_p_pos, 0, params.p_d / 2 + lengthOffset / 2);
        roofLeftDelModelObj_Temp.rotateY(Math.PI);
        roofLeftDelModelObj_Temp.updateMatrixWorld();

        const roofRightDelModelObj_Temp = roofDelModelObj.clone();
        roofRightDelModelObj_Temp.position.set(-params.leanF_p_pos, 0, -params.p_d / 2 - lengthOffset / 2);
        roofRightDelModelObj_Temp.updateMatrixWorld();

        const meshRoofTopRightTrimRidgeModel_CSG = CSG.fromMesh(meshRoofTopRightTrimRidgeModel_Temp);
        const meshRoofTopLeftTrimRidgeModel_CSG = CSG.fromMesh(meshRoofTopLeftTrimRidgeModel_Temp);

        const roofLeftDelModelObj_CSG = CSG.fromMesh(roofLeftDelModelObj_Temp)
        const roofRightDelModelObj_CSG = CSG.fromMesh(roofRightDelModelObj_Temp)
        
        let meshRoofTopRightTrimRidgeModelCSG;
        let meshRoofTopLeftTrimRidgeModelCSG;
        if(isEqualHeight){
            meshRoofTopRightTrimRidgeModelCSG = meshRoofTopRightTrimRidgeModel_CSG.subtract(roofRightDelModelObj_CSG);
            meshRoofTopLeftTrimRidgeModelCSG = meshRoofTopLeftTrimRidgeModel_CSG.subtract(roofLeftDelModelObj_CSG);
        } else {
            meshRoofTopRightTrimRidgeModelCSG = meshRoofTopRightTrimRidgeModel_CSG
            meshRoofTopLeftTrimRidgeModelCSG = meshRoofTopLeftTrimRidgeModel_CSG
        }
        const CSGRoofTopRightTrimRidgeModel = CSG.toMesh(meshRoofTopRightTrimRidgeModelCSG, new THREE.Matrix4());
        const CSGRoofTopLeftTrimRidgeModel = CSG.toMesh(meshRoofTopLeftTrimRidgeModelCSG, new THREE.Matrix4());

        rightTrimRidge = CSGRoofTopRightTrimRidgeModel.geometry;
        leftTrimRidge = CSGRoofTopLeftTrimRidgeModel.geometry;

        return {rightTrimRidge, leftTrimRidge}

    }, [params.leanF_p_d, params.leanF_p_w, params.leanF_p_h, roofWidthSize, overHangWidth, overHangHorizon, params.b_l_t_r_pF, params.p_r_s, params.p_r_p, params.p_w, params.p_h, params.p_d, roofRidgeOffsetHeight, params.o_v_h_n_g_e, params.o_v_h_n_g, angle, isEqualHeight, endOverHangSize, sideOverHangSize, params.leanF_p_pos])

    const roofModelObj = useMemo(() => {
        let mainOuter;
        let mainInner;
        let curveOuter;
        let curveInner;

        const curveInnerModel = new THREE.Shape();        
            curveInnerModel.moveTo(-0.1, 0.04);
            curveInnerModel.lineTo(-0.1, 0.04 + const_var.roofRegularDst);
            curveInnerModel.quadraticCurveTo(-0.1, const_var.roofRegularDst * 1.8 + (const_var.roofRegularDst * params.p_r_p / 12) / 10, 
                                            -0.15 + const_var.roofRegularDst, const_var.roofRegularDst * (2 + params.b_l_t_r_pF / 12) - 0.02);
            curveInnerModel.lineTo(params.leanF_p_w - 0.15, (params.leanF_p_w - 0.1) * params.b_l_t_r_pF / 12 + const_var.roofRegularDst * 2);
            curveInnerModel.lineTo(params.leanF_p_w - 0.15, (params.leanF_p_w - 0.1) * params.b_l_t_r_pF / 12 + const_var.roofRegularDst * 2 + 0.01);
            curveInnerModel.lineTo(-0.15 + const_var.roofRegularDst, const_var.roofRegularDst * (2 + params.b_l_t_r_pF / 12) - 0.01);
            curveInnerModel.quadraticCurveTo(-0.1, const_var.roofRegularDst * 1.8 + (const_var.roofRegularDst * params.p_r_p / 12) / 10, 
                                            -0.11, 0.04 + const_var.roofRegularDst);
            curveInnerModel.moveTo(-0.11, 0.04);
            curveInnerModel.closePath();
        
        const roofCutShape = new THREE.Shape();
            roofCutShape.moveTo(-roofCutShapeWidth - 2, 0);
            roofCutShape.lineTo(-roofCutShapeWidth - 2, params.leanF_p_h + (params.b_l_t_r_pF / 12*((params.leanF_p_w))) + 2);
            roofCutShape.lineTo(roofCutShapeWidth + 2, params.leanF_p_h + (params.b_l_t_r_pF / 12*((params.leanF_p_w))) + 2);
            roofCutShape.lineTo(roofCutShapeWidth + 2, 0);
            roofCutShape.closePath()

        const curveRoofCutShape = new THREE.Shape();
            curveRoofCutShape.moveTo(-params.leanF_p_w * 2, 0)
            curveRoofCutShape.lineTo(-params.leanF_p_w * 2, params.lean_p_h * 3);
            curveRoofCutShape.lineTo(params.leanF_p_w * 2, params.lean_p_h * 3);
            curveRoofCutShape.lineTo(params.leanF_p_w * 2, 0)
            curveRoofCutShape.closePath()

        let delModelPosOffsetZ = 0.75;
        let delModelPosOffsetY = 0.53;
        if(params.o_v_h_n_g_e && !params.o_v_h_n_g){
            delModelPosOffsetY = 0.53
            delModelPosOffsetZ = 1
        } else if(params.o_v_h_n_g_e && params.o_v_h_n_g){
            if(sideOverHangSize === 2 && endOverHangSize === 1){
                delModelPosOffsetZ = 1
                delModelPosOffsetY = 0.5
            } else if(sideOverHangSize === 2 && endOverHangSize === 2){
                delModelPosOffsetZ = 2
                delModelPosOffsetY = 0.53
            } else if(sideOverHangSize === 1.05 && endOverHangSize === 2){
                delModelPosOffsetZ = 1.215
                delModelPosOffsetY = 0.46
            } else if(sideOverHangSize === 1.05 || endOverHangSize === 1){
                delModelPosOffsetZ = 1
                delModelPosOffsetY = 0.5
            }
        } else{
            delModelPosOffsetZ = 0.75
            delModelPosOffsetY = 0.53
        }

        const roofHeight = params.p_r_p / 12 * params.p_w / 2 
        const roofAngle = Math.atan2(params.p_w / 2, roofHeight);

        const outerRoofMesh = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPattern(patternLength,isPattern,"roof"), extrudeSettings(patternDepth)));
        const innerRoofMesh = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPanModel, extrudeSettings(roofThickness)));
        const curveOuterMesh = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPatternCurve(2, params.b_l_t_r_pF.toString(), isPattern), extrudeSettings(curveRoofPatternDepth)))
        const curveInnerMesh = new THREE.Mesh(new THREE.ExtrudeGeometry(curveInnerModel, extrudeSettings(curveRoofPatternDepth)));

        const mainRoofCutMesh = new THREE.Mesh(new THREE.ExtrudeGeometry(roofCutShape, extrudeSettings(roofCutShapeWidth)));
        const curveRoofCutMesh = new THREE.Mesh(new THREE.ExtrudeGeometry(curveRoofCutShape, extrudeSettings(params.leanF_p_w * 2)))

        const roofDelModelObj = new THREE.Mesh(new THREE.BoxGeometry(params.p_w / 2, 1, params.p_d ), new THREE.MeshStandardMaterial({color: 'blue', side : THREE.DoubleSide}));
 
        let curveRoofCutMesh1 = curveRoofCutMesh.clone();
        curveRoofCutMesh1.rotation.set(0, -Math.PI / 4 * 3, 0);
        curveRoofCutMesh1.position.set(-(params.p_w / 2 + params.lean_p_w + overHangWidth) - params.leanF_p_pos, 0, params.p_d / 2 + params.leanF_p_w + overHangWidth + cbFrontBackWallOffset);
        curveRoofCutMesh1.updateMatrixWorld();
    
        let curveRoofCutMesh2 = curveRoofCutMesh.clone();
        curveRoofCutMesh2.rotation.set(0, Math.PI / 4 * 3, 0);
        curveRoofCutMesh2.position.set((params.p_w / 2 + params.leanR_p_w + overHangWidth) - params.leanF_p_pos, 0, (params.p_d / 2 + params.leanF_p_w + overHangWidth + cbFrontBackWallOffset));
        curveRoofCutMesh2.updateMatrixWorld();
    
        let roofCutMesh1 = mainRoofCutMesh.clone();
        roofCutMesh1.rotation.set(0, -Math.PI / 4 * 3, 0);
        roofCutMesh1.position.set(-(params.p_w / 2 + params.lean_p_w) - params.leanF_p_pos, 0, params.p_d / 2 + params.leanF_p_w + cbFrontBackWallOffset);
        roofCutMesh1.visible = false;
        roofCutMesh1.updateMatrixWorld();
        
        let roofCutMesh2 = mainRoofCutMesh.clone();
        roofCutMesh2.rotation.set(0, Math.PI / 4 * 3, 0);
        roofCutMesh2.position.set((params.p_w / 2 + params.leanR_p_w) - params.leanF_p_pos, 0, params.p_d / 2 + params.leanF_p_w + cbFrontBackWallOffset);
        roofCutMesh2.visible = false;
        roofCutMesh2.updateMatrixWorld();
    
        let outerRoofMeshPosUpdate = outerRoofMesh.clone();
        outerRoofMeshPosUpdate.position.set(patternPosition.x, patternPosition.y, patternPosition.z);
        outerRoofMeshPosUpdate.rotation.set(Math.PI / 2, (params.p_r_s === 3) ? 0 : Math.PI / -2, Math.PI);
        outerRoofMeshPosUpdate.updateMatrixWorld();
    
        // Create a temporary object to apply the parent group's transformations
        let tempGroup = new THREE.Object3D();
        tempGroup.position.set(0, params.leanF_p_h + 0.025 + regularHeightOffset, params.p_d / 2 + params.leanF_p_w + cbFrontBackWallOffset);
        tempGroup.rotation.set(Math.PI / 2 + angle, 0, 0);
        tempGroup.updateMatrixWorld();
    
        // Apply the parent's world matrix to the outerRoofMeshPosUpdate
        outerRoofMeshPosUpdate.applyMatrix4(tempGroup.matrixWorld);
        outerRoofMeshPosUpdate.updateMatrixWorld();
    
        // Now outerRoofMeshPosUpdate has the combined transformations
    
        let curveOuterMeshPosUpdate = curveOuterMesh.clone();
        curveOuterMeshPosUpdate.position.set(curveRoofPositionX, params.leanF_p_h + 0.1*(params.b_l_t_r_pF/12) + 0.035, (params.p_d / 2 + params.leanF_p_w + 0.18))
        curveOuterMeshPosUpdate.rotation.set(0, Math.PI / 2, 0)
        curveOuterMeshPosUpdate.updateMatrixWorld()
        
        let innerOuterMeshPosUpdate = curveInnerMesh.clone();
        innerOuterMeshPosUpdate.position.set(curveRoofPositionX, params.leanF_p_h + 0.1*(params.b_l_t_r_pF/12), (params.p_d / 2 + params.leanF_p_w + 0.04))
        innerOuterMeshPosUpdate.rotation.set(0, Math.PI / 2, 0)
        innerOuterMeshPosUpdate.updateMatrixWorld()

        let innerRoofMesh_Temp = innerRoofMesh.clone();
        innerRoofMesh_Temp.position.set(0, params.leanF_p_h + 0.02 - roofThickness + regularHeightOffset, params.p_d / 2 + params.leanF_p_w + cbFrontBackWallOffset + roofThickness * 2)
        innerRoofMesh_Temp.rotateX(Math.PI / 2 + angle);
        innerRoofMesh_Temp.updateMatrixWorld();
           
        const roofLeftDelModelObj_Temp = roofDelModelObj.clone();
        roofLeftDelModelObj_Temp.position.set(-params.p_w / 2 - params.leanF_p_pos, params.p_h + (params.p_r_s === 1 ? 1.42 : delModelPosOffsetY), delModelPosOffsetZ);
        roofLeftDelModelObj_Temp.rotateZ(Math.PI / 2 - roofAngle);
        roofLeftDelModelObj_Temp.updateMatrixWorld();

        const roofRightDelModelObj_Temp = roofDelModelObj.clone();
        roofRightDelModelObj_Temp.position.set(params.p_w / 2 - params.leanF_p_pos, params.p_h + (params.p_r_s === 1 ? 1.42 : delModelPosOffsetY), delModelPosOffsetZ);
        roofRightDelModelObj_Temp.rotateZ(-Math.PI / 2 + roofAngle);
        roofRightDelModelObj_Temp.updateMatrixWorld();
    
        const outerRoofMesh_CSG = CSG.fromMesh(outerRoofMeshPosUpdate);
        const innerRoofMesh_CSG = CSG.fromMesh(innerRoofMesh_Temp);
        const curveOuterMesh_CSG = CSG.fromMesh(curveOuterMeshPosUpdate)
        const curveInnerMesh_CSG = CSG.fromMesh(innerOuterMeshPosUpdate)

        const cutMesh_CSG1 = CSG.fromMesh(roofCutMesh1);
        const cutMesh_CSG2 = CSG.fromMesh(roofCutMesh2);
        const curveRoofCut_CSG1 = CSG.fromMesh(curveRoofCutMesh1)
        const curveRoofCut_CSG2 = CSG.fromMesh(curveRoofCutMesh2)

        const roofLeftDelModelObj_CSG = CSG.fromMesh(roofLeftDelModelObj_Temp)
        const roofRightDelModelObj_CSG = CSG.fromMesh(roofRightDelModelObj_Temp)
        
        let roofMeshFinalCsg
        let roofMeshFinalCsg_temp

        if(params.add_right_front_lean_porch && params.add_left_front_lean_porch) {
            roofMeshFinalCsg_temp = outerRoofMesh_CSG.subtract(cutMesh_CSG1);
            roofMeshFinalCsg = roofMeshFinalCsg_temp.subtract(cutMesh_CSG2)
        }
        else if (!params.add_right_front_lean_porch && params.add_left_front_lean_porch) roofMeshFinalCsg = outerRoofMesh_CSG.subtract(cutMesh_CSG1);
        else if (params.add_right_front_lean_porch && !params.add_left_front_lean_porch) roofMeshFinalCsg = outerRoofMesh_CSG.subtract(cutMesh_CSG2);
        else {
            roofMeshFinalCsg = outerRoofMesh_CSG
        }
        let roofMeshLeftDelFinalCSG;
        let roofMeshRightDelFinalCSG;
        let innerRoofMeshFinalCsg_temp;
        let innerRoofMeshFinalCsg;

        if(!isEqualHeight || (controls.isCheckActiveTab === 'doorwindow' && const_var.focusedSection === 'frontlean')){
            roofMeshLeftDelFinalCSG = roofMeshFinalCsg
            roofMeshRightDelFinalCSG = roofMeshLeftDelFinalCSG
            innerRoofMeshFinalCsg_temp = innerRoofMesh_CSG
            innerRoofMeshFinalCsg = innerRoofMeshFinalCsg_temp
        } else{
            roofMeshLeftDelFinalCSG = roofMeshFinalCsg.subtract(roofLeftDelModelObj_CSG);
            roofMeshRightDelFinalCSG = roofMeshLeftDelFinalCSG.subtract(roofRightDelModelObj_CSG);
            innerRoofMeshFinalCsg_temp = innerRoofMesh_CSG.subtract(roofLeftDelModelObj_CSG);
            innerRoofMeshFinalCsg = innerRoofMeshFinalCsg_temp.subtract(roofRightDelModelObj_CSG);
        }
    
        const curveOuterMesh_CSG_temp_1 = curveOuterMesh_CSG.subtract(curveRoofCut_CSG1);
        const curveOuterMesh_CSG_temp_2 = curveOuterMesh_CSG_temp_1.subtract(curveRoofCut_CSG2);
        const curveInnerMesh_CSG_temp_1 = params.add_left_front_lean_porch ? curveInnerMesh_CSG.subtract(curveRoofCut_CSG1) : curveInnerMesh_CSG;
        const curveInnerMesh_CSG_temp_2 = params.add_right_front_lean_porch ? curveInnerMesh_CSG_temp_1.subtract(curveRoofCut_CSG2) : curveInnerMesh_CSG_temp_1;
    
        const roofMeshFinal = CSG.toMesh(roofMeshRightDelFinalCSG, new THREE.Matrix4())
        const curveOuterMeshFinal = CSG.toMesh(curveOuterMesh_CSG_temp_2, new THREE.Matrix4())
        const curveInnerMeshFinal = CSG.toMesh(curveInnerMesh_CSG_temp_2, new THREE.Matrix4())

        const innerRoofMeshFinal = CSG.toMesh(innerRoofMeshFinalCsg,new THREE.Matrix4())

        mainOuter = roofMeshFinal.geometry;
        mainInner = innerRoofMeshFinal.geometry;
        curveOuter = curveOuterMeshFinal.geometry;
        curveInner = curveInnerMeshFinal.geometry;

        return {mainOuter, mainInner, curveOuter, curveInner}
        
    }, [params.b_l_t_r_pF, params.leanF_p_w, params.lean_p_h, params.p_w, params.p_r_p, patternLength, patternDepth, curveRoofPatternDepth, roofCutShapeWidth, params.p_d, params.lean_p_w, overHangWidth, params.leanF_p_pos, params.leanR_p_w, params.leanF_p_h, angle, curveRoofPositionX, params.b_l_t_r_pF, params.p_h, patternPosition, params.o_v_h_n_g_e, patternDepth, params.p_r_s, params.add_right_front_lean_porch, params.add_left_front_lean_porch, sideOverHangSize, endOverHangSize, isEqualHeight, controls.isCheckActiveTab, const_var.focusedSection, isPattern])

    useMemo(() => {
        if(params.p_h === leanRoofLeftHeight + params.lean_p_h) setIsEqualLeftHeight(true)
        else setIsEqualLeftHeight(false)    
        if(params.p_h === leanRoofRightHeight + params.leanR_p_h) setIsEqualRightHeight(true)
        else setIsEqualRightHeight(false)
        if(params.p_h === leanRoofHeight + params.leanF_p_h) setIsEqualHeight(true);   
        else setIsEqualHeight(false)
        if(params.add_left_lean && (leanRoofHeight + params.leanF_p_h === leanRoofLeftHeight + params.lean_p_h) && ((params.p_d === params.lean_p_d) || Number(params.leantoAlignmentLeft) === 2))
         setIsEqualLeftLeanHeight(true);
        else setIsEqualLeftLeanHeight(false)                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                  
        if(params.add_right_lean && (leanRoofHeight + params.leanF_p_h === leanRoofRightHeight + params.leanR_p_h) && ((params.p_d === params.leanR_p_d) || Number(params.leantoAlignmentRight) === 2))
         setIsEqualRightLeanHeight(true);
        else setIsEqualRightLeanHeight(false)
    
    
    }, [params.p_h, params.lean_p_h,leanRoofLeftHeight, leanRoofRightHeight, leanRoofHeight, params.leanR_p_h, params.b_l_t_r_p, params.b_l_t_r_pF, params.leantoAlignmentLeft, params.leantoAlignmentRight, params.b_l_t_r_pR, params.leanF_p_h, params.add_left_lean, params.add_right_lean, params.p_d, params.lean_p_d, params.leanR_p_d ])

    useMemo(() => {
        if ((isEqualLeftHeight && params.add_left_front_lean_porch)) {
            setAngle(Math.atan((leanRoofLeftHeight - trimOffsetHeight) / (params.leanF_p_w - trimThickness)))
            setRoofWidthSize(params.leanF_p_w / Math.cos(angle))
        } else if ((isEqualRightHeight && params.add_right_front_lean_porch)) {
            setAngle(Math.atan((leanRoofRightHeight - trimOffsetHeight) / (params.leanF_p_w - trimThickness)))
            setRoofWidthSize(params.leanF_p_w / Math.cos(angle))
        } else {
            setAngle(Math.atan(params.b_l_t_r_pF / 12))
            setRoofWidthSize(Math.sqrt(Math.pow(params.leanF_p_w, 2) + Math.pow((params.leanF_p_w / 12 * params.b_l_t_r_pF), 2)))
        }
            setRoofWidthSize(params.leanF_p_w / Math.cos(angle))
    }, [isEqualLeftHeight, isEqualRightHeight, params.add_left_front_lean_porch, params.add_right_front_lean_porch, leanRoofLeftHeight, leanRoofRightHeight, params.b_l_t_r_pF, params.leanF_p_w, trimOffsetHeight, angle])

    useMemo(() => {
        if (params.p_r_s === 2 || params.p_r_s === 1) {
        if(params.p_r_s === 2) {
            if (params.o_v_h_n_g === true){
                setOverHangWidth(sideOverHangSize)
            } else {
                setOverHangWidth(0.4);
            }
            if (params.o_v_h_n_g_e === true) {
                //setOverHangLength(endOverHangSize)
                setOverHangLength(endOverHangSize == 0.5 ? 0.75 : endOverHangSize )
            } else {
                setOverHangLength(0.75)
            }
        } else {
            setOverHangWidth(0)
            setOverHangLength(0.75)
        }
        if(params.p_r_s === 2) setPatternLength(diagonalLength ); else setPatternLength(diagonalLength - const_var.roofRegularDst / Math.cos(Math.atan(params.b_l_t_r_pF / 12)) + 0.03);

        if( params.add_right_front_lean_porch === true && params.add_left_front_lean_porch !== true ) {
            setPatternDepth((params.leanF_p_d) + overHangWidth + overHangLength * 2 + cbFrontBackWallOffset + params.leanF_p_w + patternLengthOffset - 0.15)
            setPatternPosition({x:(params.leanF_p_d/2) + overHangWidth + overHangLength + cbFrontBackWallOffset + params.leanF_p_w + patternPositionOffset, y:-(diagonalLength-(overHangWidth)-(roofThickness * 2)), z:0.005})
            setCurveRoofPatternDepth((params.leanF_p_d) + overHangWidth + overHangLength + cbFrontBackWallOffset + params.leanF_p_w + 0.45 + patternLengthOffset - 0.15)
            setCurveRoofPositionX( -params.leanF_p_d / 2 - 0.41+ regularPatternPositionOffset)
        } else if( params.add_right_front_lean_porch !== true && params.add_left_front_lean_porch === true ) {
            setPatternDepth((params.leanF_p_d) + overHangWidth + overHangLength * 2 + cbFrontBackWallOffset + params.leanF_p_w + patternLengthOffset / 2 - 0.15)
            setPatternPosition({x:(params.leanF_p_d/2) + overHangLength + patternPositionOffset - 0.15, y:-(diagonalLength-(overHangWidth)-(roofThickness * 2)), z:0.005})
            setCurveRoofPatternDepth((params.leanF_p_d) + overHangWidth + overHangLength + cbFrontBackWallOffset + params.leanF_p_w + 0.5 + patternLengthOffset - 0.15)
            setCurveRoofPositionX(-params.leanF_p_d / 2 - params.lean_p_w - 0.28)
        } else if( params.add_right_front_lean_porch === true && params.add_left_front_lean_porch === true ) {
            setPatternDepth((params.leanF_p_d) + (overHangWidth*2) + (cbFrontBackWallOffset*2) + (params.leanF_p_w*2))
            setPatternPosition({x:(params.leanF_p_d/2) + overHangWidth + cbFrontBackWallOffset + params.leanF_p_w, y:-(diagonalLength-(overHangWidth)-(roofThickness * 2)), z:0.005})
            setCurveRoofPatternDepth((params.leanF_p_d) + (overHangWidth*2) + (cbFrontBackWallOffset*2) + (params.leanF_p_w*2) + 1)
            setCurveRoofPositionX(-(params.p_w / 2 + params.lean_p_w) - 0.7)
        } else {
            setPatternDepth(params.leanF_p_d + ((overHangLength)*2) + patternLengthOffset - 0.3)
            setPatternPosition({x:(params.leanF_p_d + ((overHangLength)*2) )/2 + patternPositionOffset - 0.15,y:-(diagonalLength-(overHangWidth)-(roofThickness * 2) + (params.p_r_s === 1 && -0.1)) + (params.p_r_s === 1 && -0.1),z:0})
            setCurveRoofPatternDepth(params.leanF_p_d + ((overHangLength)*2) + patternLengthOffset - 0.34 - 0.3)
            setCurveRoofPositionX( -params.leanF_p_d / 2 + regularPatternPositionOffset - 0.41 )
        }
       
        } else if (params.p_r_s === 3) {  
            if (params.o_v_h_n_g === true){
                setOverHangWidth(sideOverHangSize)
            } else {
                setOverHangWidth(0.4);
            }
            if (params.o_v_h_n_g_e === true) {
                //setOverHangLength(endOverHangSize)
                setOverHangLength(endOverHangSize == 0.5 ? 0.75 : endOverHangSize )
            } else {
                setOverHangLength(0.75)
            }
            if( params.add_right_front_lean_porch === true && params.add_left_front_lean_porch !== true ) {
                setPatternLength((params.leanF_p_d) + overHangWidth + overHangLength * 2 + cbFrontBackWallOffset + params.leanF_p_w + patternLengthOffset - 0.15)
                setPatternPosition({x:(params.leanF_p_d/2) + overHangWidth + overHangLength + cbFrontBackWallOffset + params.leanF_p_w + patternPositionOffset, y:(overHangWidth)+(roofThickness * 2), z:0.005})
            } else if( params.add_right_front_lean_porch !== true && params.add_left_front_lean_porch === true ) {
                setPatternLength((params.leanF_p_d) + overHangWidth + overHangLength * 2 + cbFrontBackWallOffset + params.leanF_p_w + patternLengthOffset / 2 - 0.15)
                setPatternPosition({x:(params.leanF_p_d/2) + overHangLength + patternPositionOffset - 0.15, y:(overHangWidth)+(roofThickness * 2), z:0.005})
            } else if( params.add_right_front_lean_porch === true && params.add_left_front_lean_porch === true ) {
                setPatternLength((params.leanF_p_d) + (overHangWidth*2) + (cbFrontBackWallOffset*2) + (params.leanF_p_w*2))
                setPatternPosition({x:(params.leanF_p_d/2) + overHangWidth + cbFrontBackWallOffset + params.leanF_p_w, y:(overHangWidth)+(roofThickness * 2), z:0.005})
            } else {
                setPatternLength(params.leanF_p_d + ((overHangLength)*2) + patternLengthOffset - 0.3)
                setPatternPosition({x:(params.leanF_p_d + ((overHangLength)*2) )/2 + patternPositionOffset - 0.15,y:(overHangWidth)+(roofThickness * 2),z:0.005})
            }
            setPatternDepth(diagonalLength)
        }
    }, [params.p_r_s, params.o_v_h_n_g, params.o_v_h_n_g_e, diagonalLength, params.add_right_front_lean_porch, params.add_left_front_lean_porch, params.leanF_p_d, overHangWidth, overHangLength, params.leanF_p_w,  params.leanR_p_w, params.p_w, params.lean_p_w, patternLengthOffset, patternPositionOffset, regularPatternPositionOffset, sideOverHangSize, endOverHangSize])

    const handleMouseEvent = (e) => {
        if (controls.isCheckActiveTab === 'doorwindow' && (const_var.focusedSection === "frontlean" && params.activeWall === "F_L_R_S_W")) {
            switch (e.type) {
                case 'pointerdown':
                case 'click':
                case 'pointermove':
                    e.stopPropagation();
                    break;
                default :
                    break
            }
        }
    };
    const eventHandlers = useMouseEvents(handleMouseEvent);

    const texture = useMemo(() => {
        let texture = (const_var.wallTextureObj["vertical"] != undefined) ? const_var.wallTextureObj["vertical"].clone() : const_var.wallTextureObj["vertical"];
        textureAnisotropy(gl, texture, 1.3, 1.3, Math.PI/-2);    
        return texture
    }, [params.p_v_w,params.wallTexture,const_var.wallTextureObj[params.wallTexture]])

    return (
        <group {...eventHandlers}>
            <mesh name="roof-out" geometry={roofModelObj?.mainOuter} position={[0, 0.043, 0]} renderOrder={1}>
                <MaterialOuterRoof isPattern={isPattern} texture={texture} visibleSection="frontLean" />
            </mesh>
            {params.p_r_s !== 1 &&
                <mesh name='roof-in' geometry={roofModelObj?.mainInner} position={[0, 0, roofThickness * 2]}>
                    <meshBasicMaterial map={roofTexture}  side={THREE.DoubleSide} color={"#C9C9C9"} transparent={params.is_translusant || (controls.isCheckActiveTab == "doorwindow" && const_var.focusedSection !== 'frontLean')} opacity={0.02}  />
                </mesh>
            }
            <>
                { (params.p_r_s === 3 && params.add_right_front_lean_porch) &&
                    <mesh name='roof-top-right-trim-ridge' position={[0, 0.03, 0]} geometry={roofTopTrimRidgeModelObj?.rightTrimRidge}>
                        <MaterialRoofRidge isPattern={isPattern} visibleSection={'frontLean'} />
                    </mesh> 
                }
                { (params.p_r_s === 3 && params.add_left_front_lean_porch) &&
                    <mesh name='roof-top-left-trim-ridge' position={[0, 0.03, 0]} geometry={roofTopTrimRidgeModelObj?.leftTrimRidge} >
                        <MaterialRoofRidge isPattern={isPattern} visibleSection={'frontLean'} />
                    </mesh>
                }
            </>
            {params.p_r_s === 1 && roofModelObj?.curveOuter && roofModelObj?.curveInner &&
                <>
                    <mesh name="roof-out-front-curve" position={[0, 0.03, 0]} geometry={roofModelObj?.curveOuter} renderOrder={1}>
                        <MaterialOuterRoof isPattern={isPattern} texture={texture} visibleSection="frontLean" />
                    </mesh>
                    <mesh name="roof-inner-front" geometry={roofModelObj?.curveInner}>
                        <MaterialInnerRoof texture={horizontalMap} visibleSection={'frontLean'} />
                    </mesh>
                </>
            }
        </group>
    )
}

export default RoofModel;